import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { View, Text, StyleSheet, Pressable, Platform, type TextStyle, type ViewStyle } from 'react-native';
import Animated, { type ScrollHandlerProcessed } from 'react-native-reanimated';
import { useSafeAreaInsets } from 'react-native-safe-area-context';
import moment from 'moment';
import { appTheme, useThemeColors, type ThemeColors } from '../../shared/theme';
import { hexToRgba } from '../../shared/theme/colorUtils';
import { androidFlatSurfaceStyle, androidScaleSize } from '../../shared/theme/androidUiParity';
import {
  createStackedFooterPrimaryLabelStyle,
  createStackedFooterPrimarySurfaceStyle,
  createStackedFooterSecondaryLabelStyle,
  createStackedFooterSecondarySurfaceStyle,
} from '../../shared/theme/sharedStyles';
import { createSheetFooterDividerStyle } from '../../shared/theme/styles/sheets';
import { CalendarMonthNavRow, CalendarWeekdayStrip } from '../../components/ui';
import { horizontalLtrDirectionStyle } from '../../shared/i18n/rtl';
import { useTranslation, getLocale, formatNumber, getLocalizedWeekdaysShort } from '../../shared/i18n';
import { setDeliveryAddressDates } from '../../data/domains/addresses';
import { getToastStore } from '../../components/toast/toastStore';
import type { SubscriptionData } from '../../data/domains/subscription';
import type { Address } from '../../data/domains/addresses';
import {
  buildScheduleMonthDays,
  generateDeliveryAddressRequests,
  type ScheduleCalendarDay,
} from '../../data/domains/addresses';
import { MainLoader } from '../../components/load-main/MainLoader';
import { ensureAsciiDateString } from '../../shared/utils/apiParams';
import { normalizeCalendarDate } from '../../shared/utils/dateUtils';

const ANDROID_TEXT_COMPACT: TextStyle = Platform.OS === 'android' ? { includeFontPadding: false } : {};
const FOOTER_BTN_HEIGHT = androidScaleSize(48);
const DAY_CELL_MIN_HEIGHT = 52;

export type DeliveryScheduleCoreProps = {
  address: { id: number; name?: string };
  activeSubscriptions: SubscriptionData[];
  availableDates: string[];
  presetDates: string[];
  customerId: number;
  addresses: Address[];
  onSaved: () => void;
  onBack: () => void;
  /** Version shell wraps header + safe area; core renders scroll body + footer only when false. */
  renderHeader?: boolean;
  contentTopPadding?: number;
  contentBottomPadding?: number;
  onScroll?: ScrollHandlerProcessed;
};

function createStyles(c: ThemeColors) {
  return StyleSheet.create({
    root: { flex: 1 },
    scroll: { flex: 1 },
    scrollContent: { paddingHorizontal: appTheme.spacing.lg, paddingBottom: appTheme.spacing.md },
    instructionCard: {
      marginBottom: appTheme.spacing.sm,
      paddingHorizontal: appTheme.spacing.md,
      paddingVertical: appTheme.spacing.sm,
      borderRadius: appTheme.borderRadius.lg,
      backgroundColor: (hexToRgba(c.primary, 0.08) as string) || c.primaryUltra,
      borderWidth: StyleSheet.hairlineWidth,
      borderColor: (hexToRgba(c.primary, 0.22) as string) || c.border,
      gap: appTheme.spacing.xs,
    },
    instructionTitle: {
      ...appTheme.typography.bodyMedium,
      fontWeight: '700',
      color: c.text,
      ...ANDROID_TEXT_COMPACT,
    },
    instructionHint: {
      ...appTheme.typography.caption,
      color: c.textSecondary,
      lineHeight: 18,
      ...ANDROID_TEXT_COMPACT,
    },
    errorBanner: {
      marginBottom: appTheme.spacing.sm,
      padding: appTheme.spacing.sm,
      borderRadius: appTheme.borderRadius.md,
      backgroundColor: (hexToRgba(c.error, 0.1) as string) || c.errorFade,
      borderWidth: StyleSheet.hairlineWidth,
      borderColor: (hexToRgba(c.error, 0.25) as string) || c.error,
    },
    errorText: {
      ...appTheme.typography.caption,
      color: c.error,
      ...ANDROID_TEXT_COMPACT,
    },
    monthNavWrap: { marginBottom: appTheme.spacing.xs },
    weekDaysRow: {
      flexDirection: 'row',
      paddingHorizontal: appTheme.spacing.md,
      paddingVertical: appTheme.spacing.xs,
      marginHorizontal: appTheme.spacing.sm,
      borderRadius: appTheme.borderRadius.md,
      backgroundColor: (hexToRgba(c.text, 0.04) as string) || c.surfaceLight,
      ...horizontalLtrDirectionStyle,
    },
    weekDayCell: { flex: 1, alignItems: 'center' },
    weekDayText: {
      ...appTheme.typography.caption,
      color: c.textSecondary,
      fontWeight: '800',
      ...ANDROID_TEXT_COMPACT,
    },
    calendarGrid: {
      flexDirection: 'row',
      flexWrap: 'wrap',
      paddingHorizontal: appTheme.spacing.md,
      paddingTop: appTheme.spacing.xs,
      paddingBottom: appTheme.spacing.md,
      marginHorizontal: appTheme.spacing.sm,
      ...horizontalLtrDirectionStyle,
    },
    dayCellWrap: {
      width: '14.28%',
      minHeight: DAY_CELL_MIN_HEIGHT,
      aspectRatio: 1,
      padding: 2,
    },
    dayCell: {
      flex: 1,
      borderRadius: appTheme.borderRadius.md,
      borderWidth: StyleSheet.hairlineWidth,
      borderColor: c.borderLight,
      backgroundColor: c.surface,
      alignItems: 'center',
      justifyContent: 'center',
      padding: 2,
      gap: 2,
      ...Platform.select({ android: androidFlatSurfaceStyle() }),
    },
    dayCellDisabled: { opacity: 0.35 },
    dayCellTarget: {
      borderColor: c.primary,
      backgroundColor: (hexToRgba(c.primary, 0.12) as string) || c.primaryUltra,
    },
    dayCellOther: {
      borderColor: (hexToRgba(c.tertiary, 0.35) as string) || c.tertiaryFade,
    },
    dayNum: {
      fontSize: 13,
      fontWeight: '700',
      color: c.text,
      ...ANDROID_TEXT_COMPACT,
    },
    chip: {
      maxWidth: '100%',
      paddingHorizontal: 3,
      paddingVertical: 1,
      borderRadius: 4,
      backgroundColor: (hexToRgba(c.text, 0.08) as string) || c.surfaceLight,
    },
    chipTarget: { backgroundColor: c.primary },
    chipText: {
      fontSize: 8,
      fontWeight: '700',
      color: c.textSecondary,
      ...ANDROID_TEXT_COMPACT,
    },
    chipTextTarget: { color: c.onPrimary ?? c.white },
    footer: {
      paddingHorizontal: appTheme.spacing.lg,
      paddingTop: appTheme.spacing.sm,
      backgroundColor: c.surface,
      ...createSheetFooterDividerStyle(c),
    },
    footerRow: { flexDirection: 'row', gap: appTheme.spacing.sm },
    footerGrow: { flex: 1, minWidth: 0 },
    footerBtnShell: {
      width: '100%',
      minHeight: FOOTER_BTN_HEIGHT,
      borderRadius: appTheme.borderRadius.lg,
      alignItems: 'center',
      justifyContent: 'center',
      ...Platform.select({ android: androidFlatSurfaceStyle() }),
    },
    btnDisabled: { opacity: 0.5 },
  });
}

function resolveAddressShortName(addresses: Address[], address_id: number | null): string {
  if (address_id == null) {
    return '';
  }
  const found = addresses.find(a => a.id === address_id);
  if (!found) {
    return `#${address_id}`;
  }
  const name = found.name?.trim();
  if (name) {
    return name.length > 8 ? `${name.slice(0, 7)}…` : name;
  }
  return `#${address_id}`;
}

const ScheduleDayCell = React.memo(function ScheduleDayCell({
  day,
  target_address_id,
  addresses,
  onToggle,
  styles,
}: {
  day: ScheduleCalendarDay;
  target_address_id: number;
  addresses: Address[];
  onToggle: (date: string) => void;
  styles: ReturnType<typeof createStyles>;
}) {
  if (day.is_padding) {
    return <View style={styles.dayCellWrap} pointerEvents="none" />;
  }

  const chip_label = day.is_target_assigned
    ? resolveAddressShortName(addresses, target_address_id)
    : resolveAddressShortName(addresses, day.address_id);

  return (
    <Pressable
      style={styles.dayCellWrap}
      disabled={day.is_disabled}
      onPress={() => onToggle(day.date)}
      accessibilityRole="button"
      accessibilityState={{ selected: day.is_target_assigned, disabled: day.is_disabled }}>
      <View
        style={[
          styles.dayCell,
          day.is_disabled && styles.dayCellDisabled,
          day.is_target_assigned && styles.dayCellTarget,
          !day.is_target_assigned && day.address_id != null && styles.dayCellOther,
        ]}>
        <Text style={styles.dayNum}>{formatNumber(day.day_of_month)}</Text>
        {chip_label ? (
          <View style={[styles.chip, day.is_target_assigned && styles.chipTarget]}>
            <Text style={[styles.chipText, day.is_target_assigned && styles.chipTextTarget]} numberOfLines={1}>
              {chip_label}
            </Text>
          </View>
        ) : null}
      </View>
    </Pressable>
  );
});

export function DeliveryScheduleCore({
  address,
  activeSubscriptions,
  availableDates,
  presetDates,
  customerId,
  addresses,
  onSaved,
  onBack,
  contentTopPadding = 0,
  contentBottomPadding = 0,
  onScroll,
}: DeliveryScheduleCoreProps) {
  const { t } = useTranslation();
  const locale = getLocale();
  const insets = useSafeAreaInsets();
  const colors = useThemeColors() ?? appTheme.colors;
  const styles = useMemo(() => createStyles(colors), [colors]);
  const primary_cta = useMemo(
    () => ({
      surface: createStackedFooterPrimarySurfaceStyle(colors),
      label: createStackedFooterPrimaryLabelStyle(colors),
    }),
    [colors],
  );
  const secondary_cta = useMemo(
    () => ({
      surface: createStackedFooterSecondarySurfaceStyle(colors),
      label: createStackedFooterSecondaryLabelStyle(colors),
    }),
    [colors],
  );

  const subs = useMemo(() => (Array.isArray(activeSubscriptions) ? activeSubscriptions : []), [activeSubscriptions]);
  const [current_month, setCurrentMonth] = useState(() => moment().startOf('month'));
  const [selected_dates, setSelectedDates] = useState<string[]>([]);
  const [is_submitting, setIsSubmitting] = useState(false);
  const [inline_error, setInlineError] = useState<string | null>(null);

  useEffect(() => {
    setSelectedDates(
      presetDates
        .map(d => ensureAsciiDateString(d) || normalizeCalendarDate(d))
        .filter(Boolean),
    );
    setCurrentMonth(moment().startOf('month'));
    setInlineError(null);
  }, [address.id, presetDates]);

  const available_dates_ascii = useMemo(
    () =>
      availableDates
        .map(d => ensureAsciiDateString(d) || normalizeCalendarDate(d))
        .filter(Boolean),
    [availableDates],
  );

  const calendar_days = useMemo(
    () => buildScheduleMonthDays(current_month, available_dates_ascii, selected_dates, subs, address.id),
    [current_month, available_dates_ascii, selected_dates, subs, address.id],
  );

  const week_days = useMemo(() => getLocalizedWeekdaysShort(locale), [locale]);

  const month_floor = moment().startOf('month');
  const month_ceiling = useMemo(() => {
    if (available_dates_ascii.length === 0) {
      return moment().add(2, 'years').startOf('month');
    }
    const last = available_dates_ascii[available_dates_ascii.length - 1];
    return moment(last, 'YYYY-MM-DD', true).startOf('month');
  }, [available_dates_ascii]);

  const can_navigate_prev = current_month.clone().subtract(1, 'month').isSameOrAfter(month_floor, 'month');
  const can_navigate_next = current_month.clone().add(1, 'month').isSameOrBefore(month_ceiling, 'month');

  const toggleDate = useCallback((date_str: string) => {
    setInlineError(null);
    const ascii = ensureAsciiDateString(date_str) || normalizeCalendarDate(date_str) || date_str;
    setSelectedDates(prev => (prev.includes(ascii) ? prev.filter(d => d !== ascii) : [...prev, ascii]));
  }, []);

  const handleSave = useCallback(async () => {
    if (selected_dates.length === 0) {
      setInlineError(t('address.deliveryDatesNoSubscription'));
      return;
    }
    const requests = generateDeliveryAddressRequests(subs, selected_dates, customerId, address.id);
    if (requests.length === 0) {
      setInlineError(t('address.deliveryDatesNoSubscription'));
      return;
    }
    setIsSubmitting(true);
    setInlineError(null);
    try {
      await setDeliveryAddressDates(requests);
      getToastStore().show(t('address.deliverySavedSuccess'), { variant: 'success' });
      onSaved();
    } catch (err: unknown) {
      const message = err instanceof Error ? err.message : t('address.deliveryDatesError');
      setInlineError(message);
    } finally {
      setIsSubmitting(false);
    }
  }, [selected_dates, subs, customerId, address.id, onSaved, t]);

  const can_save = selected_dates.length > 0 && !is_submitting;

  const footer_bottom_inset = Math.max(contentBottomPadding, insets.bottom) + appTheme.spacing.sm;

  return (
    <View style={styles.root}>
      <Animated.ScrollView
        style={styles.scroll}
        contentContainerStyle={[styles.scrollContent, { paddingTop: contentTopPadding }]}
        keyboardShouldPersistTaps="handled"
        showsVerticalScrollIndicator={false}
        onScroll={onScroll}
        scrollEventThrottle={16}>
        <View style={styles.instructionCard}>
          <Text style={styles.instructionTitle}>{address.name || t('address.addressFallback')}</Text>
          <Text style={styles.instructionHint}>{t('address.deliveryScheduleExplainer')}</Text>
        </View>

        {inline_error ? (
          <View style={styles.errorBanner}>
            <Text style={styles.errorText}>{inline_error}</Text>
          </View>
        ) : null}

        <View style={styles.monthNavWrap}>
          <CalendarMonthNavRow
            currentMonth={current_month}
            canNavigatePrev={can_navigate_prev}
            canNavigateNext={can_navigate_next}
            onPrevMonth={() => can_navigate_prev && setCurrentMonth(prev => prev.clone().subtract(1, 'month'))}
            onNextMonth={() => can_navigate_next && setCurrentMonth(prev => prev.clone().add(1, 'month'))}
            previousMonthLabel={t('calendar.previousMonth')}
            nextMonthLabel={t('calendar.nextMonth')}
          />
        </View>

        <CalendarWeekdayStrip labels={week_days} rowStyle={styles.weekDaysRow} cellStyle={styles.weekDayCell} textStyle={styles.weekDayText} />

        <View style={styles.calendarGrid}>
          {calendar_days.map((day, index) => (
            <ScheduleDayCell
              key={day.is_padding ? `pad-${index}` : day.date}
              day={day}
              target_address_id={address.id}
              addresses={addresses}
              onToggle={toggleDate}
              styles={styles}
            />
          ))}
        </View>
      </Animated.ScrollView>

      <View style={[styles.footer, { paddingBottom: footer_bottom_inset }]}>
        <View style={styles.footerRow}>
          <View style={styles.footerGrow}>
            <Pressable onPress={onBack} accessibilityRole="button" accessibilityLabel={t('common.cancel')}>
              <View style={[secondary_cta.surface, styles.footerBtnShell]}>
                <Text style={secondary_cta.label}>{t('common.cancel')}</Text>
              </View>
            </Pressable>
          </View>
          <View style={styles.footerGrow}>
            <Pressable onPress={handleSave} disabled={!can_save} accessibilityRole="button" accessibilityLabel={t('common.save')}>
              <View style={[primary_cta.surface, styles.footerBtnShell, !can_save && styles.btnDisabled]}>
                {is_submitting ? (
                  <MainLoader size={18} color={colors.onPrimary ?? colors.white} />
                ) : (
                  <Text style={primary_cta.label}>{t('common.save')}</Text>
                )}
              </View>
            </Pressable>
          </View>
        </View>
      </View>
    </View>
  );
}
